import { Component, type ReactNode, type ErrorInfo } from 'react'
import { reportError, WHITE_SCREEN_MARKER } from '../lib/errorTelemetry'

// TQ #6007: React-ErrorBoundary fuer Cockpit-Robustheit. Faengt
// JS-Exceptions waehrend Render/Lifecycle ab und zeigt ein freundliches
// Fallback-UI statt die komplette App zu killen (vorher: leere weisse
// Seite ohne Sidebar).
//
// Wrapped um Routes in App.tsx — eine kaputte Seite (z.B. Downloads bei
// platforms=null) bricht jetzt nur ihren eigenen Render-Tree, nicht das
// gesamte Layout.
//
// TQ #8133: componentDidCatch meldet den Fehler jetzt zusaetzlich an die
// zentrale Error-Telemetrie (Marker [white-screen-guard]), und das Fallback-UI
// zeigt die Fehlermeldung sichtbar an, damit ein Melder den Text durchgeben
// kann. In App.tsx umhuellt eine ErrorBoundary jetzt den GESAMTEN App-Shell
// inkl. Auth-/Bootstrap-Pfad — ein synchroner Throw dort fuehrt damit zu
// diesem Fallback statt zu einem weissen Fenster.

interface Props {
  children: ReactNode
  /** Optional: callback fuer Telemetrie/Logging. */
  onError?: (error: Error, info: ErrorInfo) => void
  /** TQ #6906/#6918: Optional vollständig eigenes Fallback-UI (statt der
      Standard "Etwas ist schief gegangen" Karte). Nuetzlich für Pre-Login
      Seiten wo der User nicht versteht warum etwas crashed — er sieht statt
      Crash-Banner direkt eine Login-Alternative. */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // TQ #8133: zentral melden (console mit Marker + best-effort Telemetrie).
    reportError('react-error-boundary', error, info.componentStack ?? undefined)
    this.props.onError?.(error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    // Custom fallback (TQ #6906/#6918) — render directly statt der Crash-Karte.
    if (this.props.fallback !== undefined) {
      return this.props.fallback
    }

    return (
      <div style={{
        padding: '2.5rem 2rem',
        maxWidth: 640,
        margin: '2rem auto',
        background: '#fff',
        border: '1px solid #fee2e2',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <h2 style={{ margin: '0 0 0.75rem', color: '#991b1b', fontSize: '1.25rem' }}>
          Etwas ist schief gegangen
        </h2>
        <p style={{ color: '#4b5563', margin: '0 0 1rem', fontSize: '0.9375rem', lineHeight: 1.5 }}>
          Diese Seite konnte nicht geladen werden. Das liegt vermutlich an einem
          unerwarteten Server-Response oder einem Bug im Cockpit. Versuche es
          erneut oder lade die Seite neu. Falls das wiederholt passiert, melde
          dich beim Support.
        </p>
        {this.state.error && (
          <p style={{
            margin: '0 0 0.75rem', padding: '0.625rem 0.75rem',
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 6, fontSize: '0.8125rem', color: '#991b1b',
            fontFamily: 'monospace', wordBreak: 'break-word',
          }}>
            {/* TQ #8133: immer sichtbare Kurz-Meldung mit Marker, damit ein
                Melder den Text dem Support durchgeben kann (ohne Aufklappen). */}
            {WHITE_SCREEN_MARKER} {this.state.error.message}
          </p>
        )}
        {this.state.error && (
          <details style={{
            margin: '0.75rem 0', padding: '0.75rem',
            background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 6, fontSize: '0.8125rem', color: '#6b7280',
          }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
              Technische Details
            </summary>
            <pre style={{
              margin: '0.5rem 0 0', whiteSpace: 'pre-wrap',
              fontFamily: 'monospace', fontSize: '0.75rem',
            }}>
              {this.state.error.message}
              {this.state.error.stack && '\n\n' + this.state.error.stack.split('\n').slice(0, 5).join('\n')}
            </pre>
          </details>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            onClick={this.handleReset}
            style={{
              padding: '0.5rem 1rem', border: '1px solid #d1d5db',
              borderRadius: 6, background: '#fff', cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Erneut versuchen
          </button>
          <button
            onClick={this.handleReload}
            style={{
              padding: '0.5rem 1rem', border: 'none', borderRadius: 6,
              background: '#2563eb', color: '#fff', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 500,
            }}
          >
            Seite neu laden
          </button>
        </div>
      </div>
    )
  }
}
